import { useEffect, useState } from "react";
import { api, formatApiError } from "@/api/client";
import { fmtCurrency } from "@/lib/constants";
import { StatusBadge } from "@/components/Badges";
import { LineChart, Line, XAxis, YAxis, Tooltip, ResponsiveContainer, BarChart, Bar, Cell, CartesianGrid } from "recharts";
import {
  Ticket, Clock, CheckCircle2, DollarSign, HardHat, PackageX, TrendingUp, AlertCircle,
} from "lucide-react";
import { useNavigate } from "react-router-dom";

const CARDS = [
  { key: "todays_tickets",    label: "Today's Calls",   icon: Ticket,       accent: "text-blue-400" },
  { key: "open_tickets",      label: "Open Tickets",    icon: Clock,        accent: "text-amber-400" },
  { key: "completed_tickets", label: "Completed",       icon: CheckCircle2, accent: "text-emerald-400" },
  { key: "total_revenue",     label: "Revenue Paid",    icon: DollarSign,   accent: "text-emerald-400", money: true },
  { key: "outstanding",       label: "Outstanding",     icon: TrendingUp,   accent: "text-red-400",     money: true },
  { key: "engineers_online",  label: "Engineers Active",icon: HardHat,      accent: "text-cyan-400" },
  { key: "low_stock_count",   label: "Low Stock Items", icon: PackageX,     accent: "text-orange-400" },
  { key: "total_tickets",     label: "Total Tickets",   icon: Ticket,       accent: "text-slate-400" },
];

export default function Dashboard() {
  const [data, setData] = useState(null);
  const [recent, setRecent] = useState([]);
  const [err, setErr] = useState("");
  const navigate = useNavigate();

  useEffect(() => {
    (async () => {
      try {
        const [s, t] = await Promise.all([
          api.get("/dashboard/summary"),
          api.get("/tickets"),
        ]);
        setData(s.data);
        setRecent(t.data.slice(0, 6));
      } catch (e) { setErr(formatApiError(e)); }
    })();
  }, []);

  return (
    <div className="mx-auto max-w-[1400px] px-4 py-6 sm:px-6 lg:px-8">
      <header className="mb-6 flex items-end justify-between">
        <div>
          <p className="label-eyebrow text-primary">Command Center</p>
          <h1 className="mt-1 font-heading text-3xl font-bold tracking-tight sm:text-4xl">Dashboard</h1>
          <p className="mt-1 text-sm text-muted-foreground">Real-time overview of your field operations.</p>
        </div>
      </header>

      {err && (
        <div className="mb-4 flex items-center gap-2 rounded-md border border-destructive/40 bg-destructive/10 px-3 py-2 text-xs text-destructive">
          <AlertCircle className="h-4 w-4" /> {err}
        </div>
      )}

      {/* KPI grid */}
      <section className="grid grid-cols-2 gap-3 sm:grid-cols-3 lg:grid-cols-4">
        {CARDS.map((c) => {
          const v = data?.[c.key];
          const display = v == null ? "—" : c.money ? fmtCurrency(v) : v;
          return (
            <div key={c.key} data-testid={`kpi-${c.key}`} className="sharp-card p-4 transition-colors hover:border-primary/40">
              <div className="mb-3 flex items-center justify-between">
                <p className="label-eyebrow">{c.label}</p>
                <c.icon className={`h-4 w-4 ${c.accent}`} />
              </div>
              <p className="font-heading text-2xl font-bold tracking-tight">{display}</p>
            </div>
          );
        })}
      </section>

      {/* Charts */}
      <section className="mt-6 grid grid-cols-1 gap-4 lg:grid-cols-12">
        <div className="sharp-card p-5 lg:col-span-8">
          <div className="mb-4 flex items-center justify-between">
            <div>
              <p className="label-eyebrow">Tickets — last 7 days</p>
              <h3 className="mt-1 font-heading text-xl font-semibold">Call volume</h3>
            </div>
          </div>
          <div className="h-64">
            <ResponsiveContainer width="100%" height="100%">
              <LineChart data={data?.tickets_last_7_days || []}>
                <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" opacity={0.3} />
                <XAxis dataKey="date" tick={{ fontSize: 11, fill: "hsl(var(--muted-foreground))" }} tickFormatter={(d) => d?.slice(5)} />
                <YAxis tick={{ fontSize: 11, fill: "hsl(var(--muted-foreground))" }} allowDecimals={false} />
                <Tooltip contentStyle={{ background: "hsl(var(--card))", border: "1px solid hsl(var(--border))", borderRadius: 6, fontSize: 12 }} />
                <Line type="monotone" dataKey="count" stroke="hsl(var(--primary))" strokeWidth={2} dot={{ fill: "hsl(var(--primary))", r: 3 }} />
              </LineChart>
            </ResponsiveContainer>
          </div>
        </div>

        <div className="sharp-card p-5 lg:col-span-4">
          <p className="label-eyebrow">Tickets by status</p>
          <h3 className="mt-1 font-heading text-xl font-semibold">Pipeline</h3>
          <div className="mt-4 space-y-3">
            {Object.entries(data?.tickets_by_status || {}).map(([k, v]) => (
              <div key={k} className="flex items-center justify-between">
                <StatusBadge status={k} />
                <span className="font-mono text-sm font-medium">{v}</span>
              </div>
            ))}
            {!data?.tickets_by_status || Object.keys(data.tickets_by_status).length === 0 ? (
              <p className="text-xs text-muted-foreground">No tickets yet.</p>
            ) : null}
          </div>
        </div>

        <div className="sharp-card p-5 lg:col-span-8">
          <p className="label-eyebrow">Revenue — last 6 months</p>
          <h3 className="mt-1 font-heading text-xl font-semibold">Collected revenue</h3>
          <div className="mt-4 h-56">
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={data?.revenue_last_6_months || []}>
                <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" opacity={0.3} />
                <XAxis dataKey="month" tick={{ fontSize: 11, fill: "hsl(var(--muted-foreground))" }} />
                <YAxis tick={{ fontSize: 11, fill: "hsl(var(--muted-foreground))" }} />
                <Tooltip contentStyle={{ background: "hsl(var(--card))", border: "1px solid hsl(var(--border))", borderRadius: 6, fontSize: 12 }} formatter={(v) => fmtCurrency(v)} />
                <Bar dataKey="revenue" fill="hsl(var(--chart-2))" radius={[4, 4, 0, 0]}>
                  {(data?.revenue_last_6_months || []).map((_, i) => (
                    <Cell key={i} fill="hsl(var(--chart-2))" />
                  ))}
                </Bar>
              </BarChart>
            </ResponsiveContainer>
          </div>
        </div>

        <div className="sharp-card p-5 lg:col-span-4">
          <div className="mb-4 flex items-center justify-between">
            <div>
              <p className="label-eyebrow">Recent activity</p>
              <h3 className="mt-1 font-heading text-xl font-semibold">Latest tickets</h3>
            </div>
          </div>
          <div className="space-y-2">
            {recent.map((t) => (
              <button
                key={t.id}
                data-testid={`dashboard-recent-${t.ticket_no}`}
                onClick={() => navigate(`/app/tickets/${t.id}`)}
                className="group flex w-full items-center justify-between rounded-md border border-border/60 px-3 py-2 text-left transition-colors hover:border-primary/40 hover:bg-secondary/40"
              >
                <div className="min-w-0">
                  <p className="truncate font-mono text-xs text-muted-foreground">{t.ticket_no}</p>
                  <p className="truncate text-sm font-medium">{t.complaint}</p>
                </div>
                <StatusBadge status={t.status} />
              </button>
            ))}
            {recent.length === 0 && <p className="text-xs text-muted-foreground">No tickets yet.</p>}
          </div>
        </div>
      </section>
    </div>
  );
}
